import { FabricImage, FabricObject, Group } from 'fabric';
import type { GroupCollectionEquispacedConfig } from './types';
import { syncImageToNatural } from './natural-size';

type AxisOps = {
  mainOf: (img: FabricObject) => number;
  crossOf: (img: FabricObject) => number;
  placement: (main: number) => { left: number; top: number };
  displayCross: (obj: FabricObject) => number;
  setGroupSize: (group: Group, main: number, cross: number) => void;
};

const ROW: AxisOps = {
  mainOf: img => img.width || 1,
  crossOf: img => img.height || 1,
  placement: main => ({ left: main, top: 0 }),
  displayCross: obj => (obj.height || 0) * (obj.scaleY || 1),
  setGroupSize: (g, main, cross) => g.set({ width: main, height: cross })
};

const COLUMN: AxisOps = {
  mainOf: img => img.height || 1,
  crossOf: img => img.width || 1,
  placement: main => ({ left: 0, top: main }),
  displayCross: obj => (obj.width || 0) * (obj.scaleX || 1),
  setGroupSize: (g, main, cross) => g.set({ width: cross, height: main })
};

export function arrangeEquispaced(group: Group, cfg: GroupCollectionEquispacedConfig): void {
  const visible = group.getObjects().filter(s => s.visible !== false);
  const count = visible.length;
  if (count === 0) return;

  const axis = cfg.direction === 'row' ? ROW : COLUMN;
  const gap = cfg.gap ?? 0;
  const slot = Math.max(0, (cfg.size - (count - 1) * gap) / count);
  const cap = cfg.itemMainMax !== undefined ? Math.min(cfg.itemMainMax, slot) : slot;

  let maxCross = 0;
  visible.forEach((s, i) => {
    const center = -cfg.size / 2 + slot / 2 + i * (slot + gap);

    if (s instanceof FabricImage && typeof cfg.itemCrossSize === 'number') {
      syncImageToNatural(s);
      const scale = Math.min(cfg.itemCrossSize / axis.crossOf(s), cap / axis.mainOf(s));
      s.set({ scaleX: scale, scaleY: scale });
    }

    s.set({ ...axis.placement(center), originX: 'center', originY: 'center' });

    const c = axis.displayCross(s);
    if (c > maxCross) maxCross = c;
  });

  axis.setGroupSize(group, cfg.size, cfg.itemCrossSize ?? maxCross);
}
